import { wrapIndex } from '../core/math';
import type { RealismProfile } from '../physics/realism';
import type { KartState } from '../physics/vehicle';
import type { Track } from '../track/generate';
import { solveSpeedProfile, type SpeedProfile } from '../track/speedprofile';
import type { Corner } from '../track/brakingzones';
import type { Weather } from '../track/weather';
import { TrackQuery } from '../track/lookup';
import { mulberry32 } from '../track/rng';
import { LIVERIES, type Livery } from '../render/liveries';
import { Session } from './session';
import { Autopilot, type RacecraftView } from './autopilot';

export interface AIRacer {
  session: Session;
  pilot: Autopilot;
  livery: Livery;
  name: string;
}

export interface AIGridOptions {
  track: Track;
  /** The width-aware profile the player is scored against — same target the AI aims for. */
  profile: SpeedProfile;
  corners: Corner[];
  weather: Weather;
  realism: RealismProfile;
  tyreModel?: boolean;
  /** Salted per grid, not reused for weather/objectives — see rng convention below. */
  seed: number;
  count: number;
  /** The player's own livery, so no AI car wears it. */
  excludeLiveryId?: string;
  /**
   * The player's best recorded time on this exact circuit, or null if they
   * have never set one. Used only to seed the qualifying order below — a
   * fresh personal best from *this* attempt cannot promote you mid-race, the
   * same way a real qualifying result is fixed before lights out.
   */
  playerBestMs?: number | null;
  /**
   * Explicit grid order from a driven qualifying session. When set,
   * `buildAIGrid` places the field in this order and ignores `playerBestMs` /
   * the pace estimate. `aiRows[i]` is the grid row for `racers[i]`.
   */
  gridOrder?: { playerRow: number; aiRows: number[] };
}

/** Metres behind the previous row, and to either side of the centerline. */
const GRID_ROW_SPACING = 4.2;
const GRID_COL_OFFSET = 2.3;

/**
 * Pose for grid row `row` (0 = pole), alternating either side of the
 * centerline — a standing kart grid, not everyone stacked on the line.
 * Shared by the AI grid and the player's own placement so both come from
 * exactly the same geometry.
 */
export function gridSlotPose(track: Track, row: number): { x: number; y: number; heading: number } {
  const n = track.center.length;
  const back = Math.round((row * GRID_ROW_SPACING) / track.spacing);
  const idx = wrapIndex(track.startIndex - back, n);
  const p = track.center[idx];
  const norm = track.normal[idx];
  const side = row % 2 === 0 ? 1 : -1;
  const ahead = track.center[wrapIndex(idx + 1, n)];
  const behind = track.center[wrapIndex(idx - 1, n)];
  const heading = Math.atan2(ahead.y - behind.y, ahead.x - behind.x);
  return { x: p.x + norm.x * GRID_COL_OFFSET * side, y: p.y + norm.y * GRID_COL_OFFSET * side, heading };
}

/**
 * Where the player starts relative to the AI grid, and where each AI racer
 * (by its original index) ends up once the player is inserted.
 *
 * Ranked by an estimated qualifying time — `idealLapTime / pace` for each AI,
 * the player's own stored best for this circuit, or last on the grid if they
 * have never set one, matching how a real session without a qualifying time
 * is handled. The estimate does not need to be lap-accurate, only correctly
 * *ordered*, since ordering is all a grid position expresses.
 */
export function qualifyingOrder(
  idealLapTimeMs: number,
  aiPaces: number[],
  playerBestMs: number | null | undefined,
): { playerRow: number; aiRows: number[] } {
  const playerEstimate = playerBestMs ?? Infinity;
  const entries: Array<{ isPlayer: boolean; aiIndex: number; estimate: number }> = aiPaces.map(
    (pace, aiIndex) => ({ isPlayer: false, aiIndex, estimate: idealLapTimeMs / pace }),
  );
  entries.push({ isPlayer: true, aiIndex: -1, estimate: playerEstimate });
  entries.sort((a, b) => a.estimate - b.estimate);

  const aiRows = new Array<number>(aiPaces.length);
  let playerRow = 0;
  entries.forEach((e, row) => {
    if (e.isPlayer) playerRow = row;
    else aiRows[e.aiIndex] = row;
  });
  return { playerRow, aiRows };
}

/**
 * The AI field for a stage — identities, liveries, autopilots and per-driver
 * pace — with nothing placed on a grid yet. Deterministic per `opts.seed`
 * (salted, same convention as weather and objectives), so every player racing
 * a given Grand Prix stage faces the identical field, and a qualifying session
 * and the race that follows it agree on who is who.
 *
 * `paces[i]` is `racers[i]`'s pace multiplier, exposed so callers that order
 * the grid themselves (`qualifyingOrder`, or a real qualifying result) can.
 */
export function buildAIRoster(opts: AIGridOptions): { racers: AIRacer[]; paces: number[] } {
  const rng = mulberry32((opts.seed ^ 0x6a17f2c1) >>> 0);
  const query = new TrackQuery(opts.track);
  // Autopilot is scored against the centerline profile, not the width-aware
  // one — see the class doc — so it does not aim for racing-line speeds and
  // put itself in the grass.
  const centerlineProfile = solveSpeedProfile(opts.track, opts.weather.muAt, 0);

  const pool = LIVERIES.filter((l) => l.id !== opts.excludeLiveryId);
  const used = new Map<string, number>();

  const paces: number[] = [];
  const racers: AIRacer[] = [];
  for (let i = 0; i < opts.count; i++) {
    const livery = pool[i % pool.length];
    const n = (used.get(livery.id) ?? 0) + 1;
    used.set(livery.id, n);

    // Spread across a believable range of competence so the pack has both
    // faster and slower cars in it — a single shared pace would just be one
    // long train with no overtaking at all.
    //
    // 1.03 is not an arbitrary ceiling: measured by sweeping pace against
    // average lap time across eight seeded days, this is where average time
    // stops improving — past roughly 1.05-1.08 the same pursuit logic starts
    // overshooting corners it cannot actually track that tightly, off-track
    // excursions jump from 1-in-8 days to 5-in-8, and lap times get *worse*,
    // not better. (Re-measure this sweep after any change to grip, track
    // density, or the pursuit gains below — it is a property of all three
    // together, not a fixed number.) There is no cheap way to push a reactive
    // pursuit controller past this without chasing a full racing-line
    // optimiser, which is a much bigger rewrite than tuning this knob.
    const pace = rng.range(0.82, 1.03);
    paces.push(pace);

    // A line and a temperament per driver, drawn right after pace from the same
    // salted grid rng so the whole grid stays deterministic per stage. `apexBias`
    // skews toward the inside (tight) more often than wide, matching how a real
    // field mostly runs a conventional line with the odd momentum driver.
    const style = {
      apexBias: rng.range(-0.35, 0.62),
      aggression: rng.range(0.35, 1),
    };

    const session = new Session({
      track: opts.track,
      profile: opts.profile,
      corners: opts.corners,
      weather: opts.weather,
      query,
      realism: opts.realism,
      tyreModel: opts.tyreModel ?? false,
    });

    racers.push({
      session,
      pilot: new Autopilot(opts.track, centerlineProfile, query, pace, style),
      livery,
      name: n === 1 ? livery.name : `${livery.name} ${n}`,
    });
  }

  return { racers, paces };
}

/** Resets each AI kart and stands it on its grid slot; `aiRows[i]` is the row
 *  (0 = pole) for `racers[i]`. Player placement is the caller's job. */
export function placeAIGrid(track: Track, racers: AIRacer[], aiRows: number[]): void {
  racers.forEach((racer, i) => {
    racer.session.reset();
    const pose = gridSlotPose(track, aiRows[i]);
    racer.session.kart.x = pose.x;
    racer.session.kart.y = pose.y;
    racer.session.kart.heading = pose.heading;
  });
}

/**
 * A grid of AI-driven karts sharing the exact physics the player runs on,
 * placed on the grid ready to launch.
 *
 * Grid order is qualifying-lite by default, not a fixed pole for the player:
 * everyone, player included, is ranked by an estimated lap time and gridded in
 * that order (`qualifyingOrder`). Pass `opts.gridOrder` to place the field in
 * an explicit order instead — that is how a driven qualifying session sets the
 * race grid. `playerRow` on the return value is where the caller should place
 * the player's own kart with `gridSlotPose` — this function only owns the AI
 * cars.
 */
export function buildAIGrid(opts: AIGridOptions): { racers: AIRacer[]; playerRow: number } {
  const { racers, paces } = buildAIRoster(opts);
  const { playerRow, aiRows } =
    opts.gridOrder ??
    qualifyingOrder(opts.profile.idealLapTime * 1000, paces, opts.playerBestMs);
  placeAIGrid(opts.track, racers, aiRows);
  return { racers, playerRow };
}

/** `holdSeconds` must match whatever the player's own session launches with,
 *  so the whole grid sits through the same start-lights sequence together. */
export function launchAIGrid(racers: AIRacer[], holdSeconds = 0): void {
  for (const r of racers) r.session.launch(holdSeconds);
}

/** Beyond this an opponent is too far away to be worth reacting to. */
const RACECRAFT_RADIUS = 22; // m

/**
 * What the autopilot at `me` can see of `others` this tick: the nearest kart
 * ahead and the nearest behind, each within `RACECRAFT_RADIUS`.
 *
 * "Ahead" and "behind" are along `me`'s heading, not along the track — over a
 * handful of metres between two karts already pointing down the same road the
 * difference is nothing, and it avoids threading a `TrackQuery` through here.
 */
export function racecraftView(me: KartState, others: KartState[]): RacecraftView {
  const cosH = Math.cos(me.heading);
  const sinH = Math.sin(me.heading);
  const myFwd = me.vx * cosH + me.vy * sinH;

  let ahead: RacecraftView['ahead'];
  let behind: RacecraftView['behind'];
  let bestAhead = Infinity;
  let bestBehind = Infinity;

  for (const o of others) {
    if (o === me) continue;
    const dx = o.x - me.x;
    const dy = o.y - me.y;
    const forward = dx * cosH + dy * sinH;
    const lateral = -dx * sinH + dy * cosH;
    if (Math.hypot(forward, lateral) > RACECRAFT_RADIUS) continue;
    const oFwd = o.vx * cosH + o.vy * sinH;

    if (forward >= 0 && forward < bestAhead) {
      bestAhead = forward;
      ahead = { gap: forward, lateral, closing: myFwd - oFwd };
    } else if (forward < 0 && -forward < bestBehind) {
      bestBehind = -forward;
      behind = { gap: forward, lateral, closing: oFwd - myFwd };
    }
  }

  return { ahead, behind };
}

/**
 * Ticks every still-running AI kart one physics step, feeding each its own
 * autopilot's sampled input. Karts that have already finished (or never
 * launched) are left alone. `dragScales[i]` is that racer's slipstream
 * multiplier for this tick, if the caller is tracking one.
 *
 * `field` is every kart on track *including the player's* — pass it and each AI
 * races the cars around it (attacks a gap ahead, covers its line from a car
 * behind); omit it and every AI just drives its own line, as before.
 */
export function tickAIGrid(racers: AIRacer[], dragScales?: number[], field?: KartState[]): void {
  racers.forEach((r, i) => {
    if (r.session.phase !== 'running') return;
    const rivals = field ? racecraftView(r.session.kart, field) : undefined;
    const { steerByte, brakeByte } = r.pilot.sample(r.session.kart, rivals);
    r.session.tick(steerByte, brakeByte, dragScales?.[i] ?? 1);
  });
}
